<template>
  <form class="flex flex-col w-full" @submit.prevent="onSubmit">
    <div
      class="px-6 py-5 flex flex-col gap-6 max-h-[calc(100vh-250px)] overflow-y-auto"
    >
      <select-v2
        v-model="method"
        :options="methodOptions"
        :label="$t('INTEGRATION_SETTINGS.CLICK2CALL.REQUEST_TYPE_LABEL')"
        :hint="$t('INTEGRATION_SETTINGS.CLICK2CALL.REQUEST_TYPE_HINT')"
        :disabled="!hasEditPermission('Click2Call')"
      />

      <input-v2
        v-model.trim="baseUrl"
        maxlength="1024"
        :dir="baseUrl ? 'ltr' : null"
        :label="$t('INTEGRATION_SETTINGS.CLICK2CALL.BASE_URL_LABEL')"
        :hint="$t('INTEGRATION_SETTINGS.CLICK2CALL.BASE_URL_HINT')"
        :placeholder="
          $t('INTEGRATION_SETTINGS.CLICK2CALL.BASE_URL_PLACEHOLDER')
        "
        :disabled="!hasEditPermission('Click2Call')"
      />

      <dropdown-v2
        :value="selectedKeys"
        multiple
        searchable
        close-on-select
        :show-selection="false"
        :groups="parameterGroups"
        :label="$t('INTEGRATION_SETTINGS.CLICK2CALL.PARAMETER_LABEL')"
        :hint="$t('INTEGRATION_SETTINGS.CLICK2CALL.PARAMETER_HINT')"
        :placeholder="$t('INTEGRATION_SETTINGS.CLICK2CALL.BUTTON_PLACAEHOLDER')"
        :search-placeholder="
          $t('INTEGRATION_SETTINGS.CLICK2CALL.PARAMETER_SEARCH')
        "
        :empty-text="$t('INTEGRATION_SETTINGS.LIST.NO_RESULTS')"
        :disabled="!hasEditPermission('Click2Call')"
        @select="selectKey"
      />

      <div v-if="selectedParameters.length" class="flex flex-col gap-3 -mt-2.5">
        <div
          v-for="(param, index) in selectedParameters"
          :key="index"
          class="bg-gray-50 rounded-[8px] py-3 px-4"
        >
          <div class="flex flex-row items-center justify-between">
            <span class="text-base leading-6 text-[#000]">
              {{
                $t('INTEGRATION_SETTINGS.CLICK2CALL.PARAMETER_COUNTER_LABEL')
              }}
              {{ index + 1 }}
            </span>
            <button-v2
              variant="naked"
              color="neutral"
              size="md"
              class="!w-6 !h-6"
              icon="delete"
              :icon-class="
                hasEditPermission('Click2Call') ? 'text-[#6B7280]' : ''
              "
              :aria-label="
                $t('INTEGRATION_SETTINGS.CLICK2CALL.REMOVE_PARAMETER')
              "
              :disabled="!hasEditPermission('Click2Call')"
              @click="removeParameter(index)"
            />
          </div>
          <div class="grid grid-cols-2 gap-3 mt-3">
            <input-v2
              v-model="param.key"
              :label="$t('INTEGRATION_SETTINGS.CLICK2CALL.PARAMETER_PAIRS.KEY')"
              :disabled="!hasEditPermission('Click2Call')"
            />
            <form-field-v2
              disabled
              :label="
                $t('INTEGRATION_SETTINGS.CLICK2CALL.PARAMETER_PAIRS.VALUE')
              "
            >
              <copy-field-v2 muted :value="parameterValue(param)" />
            </form-field-v2>
          </div>
        </div>
      </div>

      <div
        v-if="!selectedParameters.length"
        class="h-px bg-gray-200 shrink-0 -mb-px"
      />
    </div>

    <modal-footer-v2
      :cancel-text="$t('INTEGRATION_SETTINGS.CLICK2CALL.SETTINGS.CANCEL')"
      :confirm-text="$t('INTEGRATION_SETTINGS.CLICK2CALL.SETTINGS.APPLY')"
      confirm-type="submit"
      :is-disabled="!isValidForm || !hasEditPermission('Click2Call')"
      :is-loading="isSubmitting"
      @cancel="onCancel"
    />

    <div v-if="isLoading" class="dd-loader">
      <spinner />
    </div>
  </form>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import Click2CallSettingsAPI from 'dashboard/api/click2callSettings';
import Spinner from 'shared/components/Spinner.vue';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import AttributeAPI from 'dashboard/api/attributes';

export default {
  name: 'Click2CallSettings',
  components: {
    Spinner,
  },
  mixins: [alertMixin, hasPermissionMixin],
  props: {
    hasHook: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      menu: [],
      customAttributes: [],
      method: 'GET',
      selectedParameters: [],
      baseUrl: undefined,
      isSubmitting: false,
      isLoading: false,
      contactFields: [
        { display: 'Name', key: 'name' },
        { display: 'Phone Number (+972)', key: 'phone_number_+972' },
        { display: 'Phone Number (972)', key: 'phone_number_972' },
        { display: 'Phone Number (05)', key: 'phone_number_05' },
        { display: 'Company', key: 'company_name' },
        { display: 'Email', key: 'email' },
      ],
    };
  },
  validations: {
    // Add validations if needed for baseUrl, etc.
  },
  computed: {
    translatedContactFields() {
      return this.contactFields.map(field => ({
        key: field.key,
        display: this.$t(
          `INTEGRATION_SETTINGS.CLICK2CALL.CONTACT_FIELDS.${field.key}`
        ),
      }));
    },
    methodOptions() {
      return [
        { value: 'GET', label: 'GET' },
        { value: 'POST', label: 'POST (JSON)' },
      ];
    },
    parameterGroups() {
      return this.menu.map(group => ({
        label: group.label,
        options: (group.children || []).map(child => ({
          value: child.key,
          label: child.display,
        })),
      }));
    },
    selectedKeys() {
      return this.selectedParameters.map(param => param.value);
    },
    isValidForm() {
      return (
        !!this.baseUrl && !!this.method && this.selectedParameters.length > 0
      );
    },
  },
  mounted() {
    this.fetchCustomAttributeDefinitions();
    this.load();
  },
  methods: {
    parameterValue(param) {
      return `{{${param.value}}}`;
    },
    hasDuplicateKeys() {
      if (!this.selectedParameters) return false;
      const keys = this.selectedParameters?.map(p => p.key);
      const uniqueKeys = new Set(keys);
      return uniqueKeys.size !== keys.length;
    },
    // sort custom attributes into groups and create a fields menu
    buildMenuFromCustomAttributes() {
      const grouped = {};

      this.customAttributes.forEach(attr => {
        const model = attr.attribute_model;

        if (!grouped[model]) {
          grouped[model] = {
            label: this.formatLabel(model),
            children: [],
          };
        }

        grouped[model].children.push({
          display: attr.attribute_display_name,
          key: attr.attribute_key,
        });
      });
      const dynamicGroups = Object.values(grouped);
      const contactFieldsGroup = {
        label: this.$t(
          `INTEGRATION_SETTINGS.CLICK2CALL.CONTACT_FIELDS.DISPLAY`
        ),
        children: this.translatedContactFields,
      };
      this.menu = [...dynamicGroups, contactFieldsGroup];
    },
    formatLabel(model) {
      const i18nKey = `INTEGRATION_SETTINGS.CLICK2CALL.ATTRIBUTES.${model}`;
      const translated = this.$t(i18nKey);
      if (translated === i18nKey) {
        // Converts snake_case like 'agent_attribute' to 'Agent Attribute'
        return model
          .replace(/_/g, ' ')
          .replace(/\b\w/g, char => char.toUpperCase());
      }
      return translated;
    },
    async fetchCustomAttributeDefinitions() {
      const response = await AttributeAPI.getAttributesByModel();
      this.customAttributes = response.data;
      this.buildMenuFromCustomAttributes();
    },

    selectKey(selectedKey) {
      if (!selectedKey) {
        return;
      }

      // Check if the parameter already exists in the selectedParameters array
      const isDuplicate = this.selectedParameters.some(
        param => param.key === selectedKey
      );
      if (isDuplicate) {
        this.showAlert(
          this.$t(`INTEGRATION_SETTINGS.CLICK2CALL.DUPLICATE_ERROR`)
        );
        return;
      }

      this.selectedParameters.push({
        key: selectedKey,
        value: selectedKey,
      });
    },
    // Removes a parameter from the selected list
    removeParameter(index) {
      this.selectedParameters.splice(index, 1);
    },

    onCancel() {
      this.$emit('cancel');
    },

    async onSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      if (this.hasDuplicateKeys()) {
        this.showAlert(
          this.$t(`INTEGRATION_SETTINGS.CLICK2CALL.DUPLICATE_KEYS_ERROR`)
        );
        return;
      }
      const payload = {
        enable: true,
        base_url: this.baseUrl,
        method: this.method,
        selected_parameters: this.selectedParameters.map(param => ({
          key: param.key,
          value: param.value,
        })),
      };

      this.isSubmitting = true;
      try {
        await Click2CallSettingsAPI.setSettings(payload);
        if (!this.hasHook) {
          await this.$store.dispatch('integrations/createHook', {
            app_id: 'click_to_call',
            settings: {},
          });
        }
        this.showAlert(this.$t(`INTEGRATION_SETTINGS.CLICK2CALL.SEND_SUCCESS`));
        this.$emit('saved');
        this.onCancel();
      } catch (err) {
        this.showAlert(this.$t('PLATFORM.API.SAVE_ERROR'));
      } finally {
        this.isSubmitting = false;
      }
    },

    async load() {
      this.isLoading = true;
      try {
        const { data } = await Click2CallSettingsAPI.getSettings();
        this.baseUrl = data?.base_url || undefined;
        this.method = data?.method || 'GET';
        this.selectedParameters = data.selected_parameters || [];
      } catch (err) {
        this.showAlert(this.$t('PLATFORM.API.GET_ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
